import { createFileRoute, Link } from '@tanstack/react-router'
import { ChartLine, Landmark, Plus, Wallet } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { balancesQueryOptions } from '@/features/bank-accounts/api'
import { CashPanel } from '@/features/dashboard/cash-panel'
import { ClassificacaoPanel } from '@/features/dashboard/classificacao-panel'
import { hojeIso } from '@/features/dashboard/datas'
import { MonthlyBars } from '@/features/dashboard/monthly-bars'
import { janelaDeMeses } from '@/features/dashboard/resumo-series'
import { useSaldoFluxo } from '@/features/dashboard/use-saldo-fluxo'
import { IntegrationStatusBadge } from '@/features/integrations/components/integration-status-badge'
import { formatBusinessDate, parseISODate } from '@/features/schedules/components/date-text'
import { MoneyText } from '@/features/schedules/components/money-text'
import { fromCents, isMoney, toCents, type Cents } from '@/shared/money/money'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { EmptyState } from '@/shared/ui/empty-state'
import { MonthRangePicker, type MesRange } from '@/shared/ui/month-range-picker'
import { PrintButton } from '@/shared/ui/print-button'
import { Skeleton } from '@/shared/ui/skeleton'
import { PageHeader } from '@/widgets/app-shell/page-header'
import { LineChart, type ChartSeries } from '@/widgets/chart/line-chart'
import { PrintHeader } from '@/widgets/print/print-header'

export const Route = createFileRoute('/_authed/gestao-de-caixa/resumo')({
  loader: ({ context }) => context.queryClient.ensureQueryData(balancesQueryOptions()),
  component: ResumoPage,
})

/** Rótulo de mês/ano a partir de uma data ISO. */
function formatMes(iso: string, lang: string): string {
  const d = parseISODate(iso.length === 7 ? `${iso}-01` : iso)
  return new Intl.DateTimeFormat(lang, { month: 'short', year: 'numeric' }).format(d)
}

/** number decimal em centavos → tom pelo sinal (negativo = vermelho). */
function tonePorSinal(cents: number): 'expense' | 'neutral' {
  return cents < 0 ? 'expense' : 'neutral'
}

function ResumoPage() {
  const { t, i18n } = useTranslation(['reports', 'nav', 'common', 'bankAccounts'])
  const lang = i18n.language
  // Faixa de meses do Fluxo (seletor estilo Nibo). Default = mês atual; nasce de
  // `hoje` a cada carga, então recarregar sempre volta ao mês corrente.
  const mesAtual = hojeIso().slice(0, 7)
  const [rangeMes, setRangeMes] = useState<MesRange>({ from: mesAtual, to: mesAtual })
  const janelaFluxo = useMemo(
    () => janelaDeMeses(rangeMes.from, rangeMes.to),
    [rangeMes.from, rangeMes.to],
  )

  // Saldo + Fluxo de caixa: MESMA lógica da tela de Fluxo (hook compartilhado) —
  // contas/integrações selecionáveis por checkbox, saldo e série do gráfico.
  const {
    hojeIsoStr,
    contasManuais,
    integradas,
    estaMarcada,
    alternar,
    saldoIntegracao,
    abertasIntegracao,
    refPorConta,
    saldoHojeCents,
    chartPoints,
    evolMensal,
    entriesDaJanela,
    schedulesPagosDaJanela,
    txConsignado,
    mesAtualIso,
    mesInicioJanela,
    graficoCarregando,
    evolLoading,
    balancesLoading,
  } = useSaldoFluxo(janelaFluxo)

  const semContas = contasManuais.length === 0 && integradas.length === 0

  const dinheiroCompacto = (cents: number) =>
    new Intl.NumberFormat(lang, {
      style: 'currency',
      currency: 'BRL',
      notation: 'compact',
      maximumFractionDigits: 2,
    }).format(cents / 100)

  const series: readonly ChartSeries[] = [
    {
      id: 'saldo',
      label: t('reports:resumo.balanceLine'),
      color: 'var(--color-action)',
      negativeColor: 'var(--color-expense)',
      area: true,
      points: chartPoints,
    },
  ]

  return (
    <>
      <PageHeader title={t('nav:cash.summary')} actions={<PrintButton />} />

      <div className="flex flex-col gap-4 p-6">
        <PrintHeader title={t('nav:cash.summary')} />
        {/* --- Saldo + Fluxo de caixa --- */}
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Saldo */}
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <Wallet className="text-ink-muted size-5" aria-hidden />
                <CardTitle>{t('reports:resumo.balance')}</CardTitle>
              </div>
              <MoneyText
                value={fromCents(saldoHojeCents as Cents)}
                emphasis
                tone={tonePorSinal(saldoHojeCents)}
              />
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Link
                to="/gestao-de-caixa/contas-e-extratos"
                className="text-action hover:text-action-hover text-xs"
              >
                {t('reports:resumo.manageAccounts')} →
              </Link>

              {balancesLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : semContas ? (
                <EmptyState
                  icon={Wallet}
                  title={t('reports:noAccounts.title')}
                  description={t('reports:noAccounts.description')}
                />
              ) : (
                <ul className="divide-line divide-y">
                  {contasManuais.map((s) => (
                    <li key={s.bank_account.id} className="flex items-center gap-3 py-2">
                      <input
                        type="checkbox"
                        checked={estaMarcada(s.bank_account.id)}
                        onChange={() => {
                          alternar(s.bank_account.id)
                        }}
                        aria-label={s.bank_account.name}
                        className="size-4 shrink-0 rounded"
                        style={{ accentColor: 'var(--color-action)' }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-ink truncate text-sm">{s.bank_account.name}</p>
                        {refPorConta.has(s.bank_account.id) && (
                          <p className="text-ink-subtle truncate text-xs">
                            {refPorConta.get(s.bank_account.id)}
                          </p>
                        )}
                      </div>
                      <MoneyText
                        value={s.balance}
                        tone={isMoney(s.balance) ? tonePorSinal(toCents(s.balance)) : 'neutral'}
                      />
                    </li>
                  ))}
                  {/* Integrações (Hatch etc.) = contas: checkbox + saldo REALIZADO. */}
                  {integradas.map((i, idx) => {
                    const saldo = saldoIntegracao(idx)
                    return (
                      <li key={i.id} className="flex items-center gap-3 py-2">
                        <input
                          type="checkbox"
                          checked={estaMarcada(i.id)}
                          onChange={() => {
                            alternar(i.id)
                          }}
                          aria-label={i.name}
                          className="size-4 shrink-0 rounded"
                          style={{ accentColor: 'var(--color-action)' }}
                        />
                        <Landmark className="text-ink-subtle size-4 shrink-0" aria-hidden />
                        <div className="min-w-0 flex-1">
                          <p className="text-ink truncate text-sm">{i.name}</p>
                          <div className="mt-0.5 flex items-center gap-1.5">
                            <Badge tone="info">{t('bankAccounts:nature.integrated')}</Badge>
                            <IntegrationStatusBadge status={i.status} />
                          </div>
                        </div>
                        <MoneyText value={fromCents(saldo as Cents)} tone={tonePorSinal(saldo)} />
                      </li>
                    )
                  })}
                </ul>
              )}

              <Button variant="ghost" size="sm" asChild className="self-start">
                <Link to="/gestao-de-caixa/contas-e-extratos">
                  <Plus aria-hidden />
                  {t('reports:resumo.addAccount')}
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* Fluxo de caixa */}
          <Card>
            <CardHeader className="flex-row items-start justify-between">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <ChartLine className="text-ink-muted size-5" aria-hidden />
                  <CardTitle>{t('reports:resumo.cashFlow')}</CardTitle>
                </div>
                <Link
                  to="/gestao-de-caixa/fluxo-de-caixa"
                  className="text-action hover:text-action-hover text-xs"
                >
                  {t('reports:resumo.manageCashFlow')} →
                </Link>
              </div>
              <MonthRangePicker
                value={rangeMes}
                onChange={setRangeMes}
                ariaLabel={t('reports:resumo.cashFlow')}
              />
            </CardHeader>
            <CardContent>
              {graficoCarregando ? (
                <Skeleton className="h-56 w-full" />
              ) : (
                <LineChart
                  series={series}
                  ariaLabel={t('reports:resumo.chartAria')}
                  formatValue={dinheiroCompacto}
                  formatDate={(iso) => formatBusinessDate(iso, lang)}
                  todayX={hojeIsoStr}
                  todayLabel={t('reports:resumo.today')}
                  height={224}
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* --- Recebimentos / Pagamentos --- */}
        <div className="grid gap-4 lg:grid-cols-2">
          <CashPanel kind="receivable" extras={abertasIntegracao('receivable')} />
          <CashPanel kind="payable" extras={abertasIntegracao('payable')} />
        </div>

        {/* --- Resultado Operacional --- */}
        <Card>
          <CardHeader>
            <CardTitle>{t('reports:operating.title')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-6 lg:grid-cols-2">
            <div className="flex flex-col gap-3">
              <h3 className="text-ink-muted text-xs font-semibold uppercase">
                {t('reports:operating.monthly')}
              </h3>
              {evolLoading ? (
                <Skeleton className="h-40 w-full" />
              ) : (
                <MonthlyBars
                  data={evolMensal}
                  formatValue={dinheiroCompacto}
                  formatPeriod={(p) => formatMes(p, lang)}
                />
              )}
            </div>
            <div className="flex flex-col gap-3">
              <h3 className="text-ink-muted text-xs font-semibold uppercase">
                {t('reports:operating.classification')}
              </h3>
              {/* Donut do realizado do mês por categoria (contábil + consignado do
                  Hatch mapeado por direção), somando só as contas MARCADAS. */}
              {evolLoading ? (
                <Skeleton className="h-40 w-full" />
              ) : (
                <ClassificacaoPanel
                  entries={entriesDaJanela}
                  schedulesPagos={schedulesPagosDaJanela}
                  txConsignado={txConsignado}
                  mesAtual={mesAtualIso}
                  mesMin={mesInicioJanela}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
